
import { useState, useEffect } from 'react';
import { ref, onValue, set, update, remove, get } from 'firebase/database';
import { database } from '../firebase/firebase';
import {Event, CreateEventData, EventMeta} from '../types';
import { generateId } from '../utils/helpers';
import {t} from "i18next";

export interface EventWithId extends Event {
    id: string;
}

export const useEvents = (userId?: string) => {
    const [events, setEvents] = useState<EventWithId[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        // Listen to userEvents list first
        const userEventsRef = ref(database, `userEvents/${userId}`);
        const unsubscribe = onValue(
            userEventsRef,
            async (snapshot) => {
                try {
                    const userEventsList = snapshot.val();
                    if (!userEventsList) {
                        setEvents([]);
                        setError(null);
                        setLoading(false);
                        return;
                    }

                    // Get list of event IDs for this user
                    const eventIds = Object.keys(userEventsList);

                    // Fetch each event's data with expenses
                    const userEvents: EventWithId[] = [];
                    await Promise.all(
                        eventIds.map(async (eventId) => {
                            try {
                                // Fetch event meta and settlement data
                                const eventRef = ref(database, `events/${eventId}`);
                                const eventSnapshot = await get(eventRef);

                                // Fetch expenses separately
                                const expensesRef = ref(database, `eventExpenses/${eventId}`);
                                const expensesSnapshot = await get(expensesRef);

                                if (eventSnapshot.exists()) {
                                    const eventData = eventSnapshot.val();
                                    const expenses = expensesSnapshot.exists() ? expensesSnapshot.val() : {};

                                    // Combine event data with expenses to maintain Event interface
                                    const completeEvent: Event = {
                                        ...eventData,
                                        expenses: expenses
                                    };

                                    userEvents.push({ ...completeEvent, id: eventId });
                                }
                            } catch (err) {
                                console.error(`Error loading event ${eventId}:`, err);
                            }
                        })
                    );

                    // Sort by creation date (newest first)
                    userEvents.sort((a, b) => b.meta.createdAt - a.meta.createdAt);
                    setEvents(userEvents);
                    setError(null);
                } catch (err) {
                    setError('Failed to load events');
                    console.error('Error loading events:', err);
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                setError('Failed to load events');
                setLoading(false);
                console.error('Firebase error:', err);
            }
        );

        return () => unsubscribe();
    }, [userId]);


    const createEvent = async (eventData: CreateEventData, eventId?: string): Promise<string> => {
        const finalEventId = eventId || `ev_${generateId()}`;
        const userId = eventData.meta.createdBy;

        // Separate expenses from event data
        const { expenses, ...eventWithoutExpenses } = eventData;

        // Create the main event (without expenses)
        const eventRef = ref(database, `events/${finalEventId}`);
        await set(eventRef, eventWithoutExpenses);

        // Create expenses separately if they exist
        if (expenses && Object.keys(expenses).length > 0) {
            const expensesRef = ref(database, `eventExpenses/${finalEventId}`);
            await set(expensesRef, expenses);
        }

        // Add to eventList with active status (true = active, false = closed)
        const eventListRef = ref(database, `eventList/${finalEventId}`);
        await set(eventListRef, true); // Always start as active

        // Add to userEvents for efficient querying
        const userEventRef = ref(database, `userEvents/${userId}/${finalEventId}`);
        await set(userEventRef, true);

        // Create eventUsers for all participants
        const participantsMap = eventData.meta.participants;
        if (participantsMap && Object.keys(participantsMap).length > 0) {
            await Promise.all(
                Object.keys(participantsMap).map(async (participantId: string) => {
                    const isAnonymous = participantId.startsWith('~u_');

                    if (isAnonymous) {
                        return; // Skip - anonymous users already created
                    }

                    // Only create eventUser for registered users
                    const eventUser: any = {
                        userId: participantId,
                        displayName: participantsMap[participantId],
                        isAnonymous: false,
                        createdAt: Date.now(),
                        lastAccess: Date.now()
                    };

                    // Copy bank info from users table if it's a registered user.
                    // The name is NOT copied: whatever was typed on the create form is
                    // this event's name, and overwriting it with the account name threw
                    // that choice away the moment the event was created.
                    if (!isAnonymous) {
                        try {
                            const userRef = ref(database, `users/${participantId}`);
                            const userSnapshot = await get(userRef);
                            if (userSnapshot.exists()) {
                                const userProfile = userSnapshot.val();
                                if (userProfile.bankInfo) {
                                    eventUser.bankInfo = userProfile.bankInfo;
                                }
                            }
                        } catch (error) {
                            console.error('Error copying bank info:', error);
                        }
                    }

                    const eventUserRef = ref(database, `eventUsers/${finalEventId}/${participantId}`);
                    await set(eventUserRef, eventUser);
                })
            );
        }

        return finalEventId;
    };

    const deleteEvent = async (eventId: string): Promise<void> => {
        // Get event data to find owner
        const eventRef = ref(database, `events/${eventId}`);
        const eventSnapshot = await get(eventRef);

        if (eventSnapshot.exists()) {
            const eventData = eventSnapshot.val();
            const ownerId = eventData.meta.createdBy;

            // Remove from all locations including expenses and eventUsers
            await Promise.all([
                remove(ref(database, `events/${eventId}`)),
                remove(ref(database, `eventExpenses/${eventId}`)),
                remove(ref(database, `eventUsers/${eventId}`)),
                remove(ref(database, `eventList/${eventId}`)),
                remove(ref(database, `userEvents/${ownerId}/${eventId}`))
            ]);
        }
    };

    const removeEventHistory = async (userId: string | undefined, eventId: string): Promise<void> => {
        if (userId) {
            await remove(ref(database, `userEventHistory/${userId}/${eventId}`));
        }

        // Signed-in visits keep a local copy too, which is the list shown after
        // signing out — so it goes as well, or the event would come back there.
        const localHistory = localStorage.getItem('eventHistory');
        if (localHistory) {
            try {
                const parsed = JSON.parse(localHistory);
                delete parsed[eventId];
                localStorage.setItem('eventHistory', JSON.stringify(parsed));
            } catch (err) {
                console.error('Error removing local history:', err);
            }
        }
    };

    return { events, loading, error, createEvent, deleteEvent, removeEventHistory };
};

export const useEvent = (eventId?: string) => {
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!eventId) {
            setLoading(false);
            return;
        }

        // Listen to both event and expenses changes
        const eventRef = ref(database, `events/${eventId}`);
        const expensesRef = ref(database, `eventExpenses/${eventId}`);

        let eventData: any = null;
        let expensesData: any = {};
        let eventLoaded = false;
        let expensesLoaded = false;

        const updateEvent = () => {
            if (eventLoaded && expensesLoaded && eventData) {
                const completeEvent: Event = {
                    ...eventData,
                    expenses: expensesData || {}
                };
                setEvent(completeEvent);
                setError(null);
                setLoading(false);
            } else if (eventLoaded && !eventData) {
                setEvent(null);
                setError(null);
                setLoading(false);
            }
        };

        // Listen to event changes
        const unsubscribeEvent = onValue(
            eventRef,
            (snapshot) => {
                try {
                    eventData = snapshot.val();
                    eventLoaded = true;
                    updateEvent();
                } catch (err) {
                    setError('Failed to load event');
                    console.error('Error loading event:', err);
                    setLoading(false);
                }
            },
            (err) => {
                setError('Failed to load event');
                setLoading(false);
                console.error('Firebase error:', err);
            }
        );

        // Listen to expenses changes
        const unsubscribeExpenses = onValue(
            expensesRef,
            (snapshot) => {
                try {
                    expensesData = snapshot.val() || {};
                    expensesLoaded = true;
                    updateEvent();
                } catch (err) {
                    console.error('Error loading expenses:', err);
                    // Don't fail the whole operation if expenses fail to load
                    expensesData = {};
                    expensesLoaded = true;
                    updateEvent();
                }
            },
            (err) => {
                console.error('Firebase expenses error:', err);
                // Don't fail the whole operation if expenses fail to load
                expensesData = {};
                expensesLoaded = true;
                updateEvent();
            }
        );

        return () => {
            unsubscribeEvent();
            unsubscribeExpenses();
        };
    }, [eventId]);

    const updateEvent = async (eventId: string, updates: Partial<EventMeta>) => {
        if (!eventId) return;
        const metaRef = ref(database, `events/${eventId}/meta`);
        await update(metaRef, updates);
    };

    const addParticipant = async (eventId: string, participantName: string) => {
        if (!eventId || !participantName.trim()) return;

        try {
            // Generate anonymous ID for new participant
            const participantId = `~u_${generateId()}`;
            const displayName = participantName.trim();

            // ✅ 1. Add to event meta participants only
            const participantsRef = ref(database, `events/${eventId}/meta/participants`);

            // Get current participants
            const participantsSnapshot = await get(participantsRef);
            const currentParticipants = participantsSnapshot.exists() ? participantsSnapshot.val() : [];

            const updatedParticipants = [...currentParticipants, participantId];
            await set(participantsRef, updatedParticipants);

            // ✅ 2. Create eventUser record
            const eventUser = {
                userId: participantId,
                displayName,
                isAnonymous: true,
                createdAt: Date.now(),
                lastAccess: Date.now()
            };

            const eventUserRef = ref(database, `eventUsers/${eventId}/${participantId}`);
            await set(eventUserRef, eventUser);

            return participantId;
        } catch (error) {
            console.error('Error adding participant:', error);
            throw error;
        }
    };

    const removeParticipant = async (eventId: string, participantId: string) => {
        if (!eventId || !participantId) return;

        try {
            // Check if participant has any expenses
            const expensesRef = ref(database, `eventExpenses/${eventId}`);
            const expensesSnapshot = await get(expensesRef);
            const expenses = expensesSnapshot.val() || {};

            let hasExpenses = false;

            // Check if participant appears in any expense
            Object.values(expenses).forEach((expense: any) => {
                if (expense.paidBy === participantId ||
                    (expense.splitBetween && expense.splitBetween.includes(participantId)) ||
                    (expense.beneficiaries && expense.beneficiaries.includes(participantId))) {
                    hasExpenses = true;
                }
            });

            // If participant has expenses, show confirmation dialog
            if (hasExpenses) {
                const confirmMessage = t ? t('event.editEvent.form.participantRemoval.confirmMessage') : 'This participant is involved in existing expenses. Removing them will also remove them from all expense records. Do you want to continue?';
                if (!window.confirm(confirmMessage)) {
                    return false;
                }

                // Remove participant from all expenses
                const updates: { [key: string]: any } = {};

                Object.entries(expenses).forEach(([expenseId, expense]: [string, any]) => {

                    // If participant was payer, we need to handle this carefully
                    if (expense.paidBy === participantId) {
                        // You might want to reassign to event owner or handle differently
                        // For now, we'll remove the expense entirely
                        updates[`eventExpenses/${eventId}/${expenseId}`] = null;
                    }

                    // Remove from splitBetween array
                    if (expense.splitBetween && expense.splitBetween.includes(participantId)) {
                        const updatedSplitBetween = expense.splitBetween.filter((id: string) => id !== participantId);
                        if (updatedSplitBetween.length > 0) {
                            updates[`eventExpenses/${eventId}/${expenseId}/splitBetween`] = updatedSplitBetween;
                        } else {
                            // If no one left to split with, remove expense
                            updates[`eventExpenses/${eventId}/${expenseId}`] = null;
                        }
                    }

                    // Remove from beneficiaries array (for sponsor expenses)
                    if (expense.beneficiaries && expense.beneficiaries.includes(participantId)) {
                        const updatedBeneficiaries = expense.beneficiaries.filter((id: string) => id !== participantId);
                        if (updatedBeneficiaries.length > 0) {
                            updates[`eventExpenses/${eventId}/${expenseId}/beneficiaries`] = updatedBeneficiaries;
                        } else {
                            // If no beneficiaries left, remove expense
                            updates[`eventExpenses/${eventId}/${expenseId}`] = null;
                        }
                    }
                });

                // Apply expense updates
                if (Object.keys(updates).length > 0) {
                    await update(ref(database), updates);
                }
            }

            // Remove participant from event meta
            const eventRef = ref(database, `events/${eventId}/meta/participants`);
            const participantsSnapshot = await get(eventRef);

            if (participantsSnapshot.exists()) {
                const participants = participantsSnapshot.val() || {};

                // Remove participant from participants object
                delete participants[participantId];
                await set(eventRef, participants);
            }

            // A ratio left behind would apply again if the same account rejoined
            await remove(ref(database, `events/${eventId}/meta/shareRatios/${participantId}`));

            // Remove from eventUsers
            const eventUserRef = ref(database, `eventUsers/${eventId}/${participantId}`);
            await remove(eventUserRef);

            return true;
        } catch (error) {
            console.error('Error removing participant:', error);
            throw error;
        }
    };

    const updateParticipantName = async (eventId: string, participantId: string, newName: string) => {
        if (!eventId || !participantId || !newName.trim()) return false;
        try {
            // Update in event meta if it exists
            const eventRef = ref(database, `events/${eventId}/meta/participants`);
            const participantsSnapshot = await get(eventRef);
            if (participantsSnapshot.exists()) {
                const participants = participantsSnapshot.val() || {};
                if (participants[participantId]) {
                    participants[participantId] = newName.trim();
                    await set(eventRef, participants);
                }
            }

            // Update in eventUsers
            const eventUserRef = ref(database, `eventUsers/${eventId}/${participantId}`);
            const userSnapshot = await get(eventUserRef);
            if (userSnapshot.exists()) {
                await update(eventUserRef, { displayName: newName.trim() });
            }
            return true;
        } catch (error) {
            console.error('Error updating participant name:', error);
            throw error;
        }
    };

    const deleteEvent = async () => {
        if (!eventId) return;

        // Get event data to find owner
        const eventRef = ref(database, `events/${eventId}`);
        const eventSnapshot = await get(eventRef);

        if (eventSnapshot.exists()) {
            const eventData = eventSnapshot.val();
            const ownerId = eventData.meta.createdBy;

            // Remove from all locations including expenses and eventUsers
            await Promise.all([
                remove(ref(database, `events/${eventId}`)),
                remove(ref(database, `eventExpenses/${eventId}`)),
                remove(ref(database, `eventUsers/${eventId}`)), // NEW: Remove all eventUsers
                remove(ref(database, `eventList/${eventId}`)),
                remove(ref(database, `userEvents/${ownerId}/${eventId}`))
            ]);
        }
    };

    return { event, loading, error, updateEvent, addParticipant, removeParticipant, updateParticipantName, deleteEvent };
};